import { useCreateAdvertisementMutation } from "@/app/features/advertisement/advertisementApiSlice";
import { useGetPopularSearchCategoriesQuery } from "@/app/features/popularSearch/popularSearchApiSlice";
import CreatableAutocomplete from "@/components/forms/FormAutoComplete";
import FormImageInput from "@/components/forms/FormImageInput";
import FormInput from "@/components/forms/FormInput";
import MultiSelectField from "@/components/forms/FormMultiSelect";
import FormSelect from "@/components/forms/FormSelect";
import FormSwitch from "@/components/forms/FormSwitch";
import bannerTypes, {
  bannerPostions,
  bannerType,
  frontendPages,
  pageLists,
} from "@/constants/bannertype";
import states from "@/constants/states";
import {
  Box,
  Button,
  Container,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { Form, Formik, useFormikContext } from "formik";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";

const initialValues = {
  adName: "",
  adType: bannerType.GenericAD,
  bannerPosition: "BOTH",
  pageName: "",
  webLink: "",
  status: true,
  state: "All",
  models: [],
};

const validationSchema = Yup.object().shape({
  adName: Yup.string()
    .required()
    .min(2)
    .max(50)
    .label("Advertisement Title")
    .trim(),
  webLink: Yup.string().required().min(5).label("Advertisement Detail").trim(),
  adType: Yup.string().required().min(1).label("Advertisement Type").trim(),
  bannerPosition: Yup.string().label("Banner Position").required(),
  pageName: Yup.string()
    .label("Page Name")
    .test("test", "Page Name is required", function (value) {
      if (this.parent.adType && this.parent.adType === bannerType.TargetedAD) {
        return value ? true : false;
      }
      return true;
    }),
  state: Yup.string().required().min(1).label("Advertisement State").trim(),
  status: Yup.boolean().required().label("Advertisement Status"),
  desktopBanner: Yup.mixed()
    .required("Desktop Banner is require")
    .test(
      "type",
      "Only the following formats are accepted: .jpg, .webp and.png",
      function (value) {
        return (
          value &&
          (value.type === "image/png" ||
            value.type === "image/jpg" ||
            value.type === "image/jpeg" ||
            value.type === "image/webp")
        );
      }
    )
    .label(""),
  mobileBanner: Yup.mixed()
    .required("Mobile Banner is required")
    .test(
      "type",
      "Only the following formats are accepted: .jpg, .webp and.png",
      (value) => {
        return (
          value &&
          (value.type === "image/png" ||
            value.type === "image/jpg" ||
            value.type === "image/jpeg" ||
            value.type === "image/webp")
        );
      }
    )
    .label(""),
  models: Yup.array()
    .of(Yup.string())
    .label("Models Name")
    .test("test", "Models is required", function (value) {
      if (this.parent.adType && this.parent.adType === bannerType.TargetedAD) {
        return value ? true : false;
      }
      return true;
    }),
});

export default function CreateAdvertisementPage() {
  const [createPost, { isLoading }] = useCreateAdvertisementMutation();
  const {
    data,
    isLoading: isPopularCategoryLoading,
    isError,
    error,
  } = useGetPopularSearchCategoriesQuery();

  const navigate = useNavigate();

  const handleSubmit = async (inputs) => {
    try {
      const formData = new FormData();
      formData.append("adName", inputs.adName);
      formData.append("adType", inputs.adType);
      formData.append("bannerPosition", inputs.bannerPosition);
      formData.append("webLink", inputs.webLink);

      formData.append("images", inputs.desktopBanner);
      formData.append("images", inputs.mobileBanner);

      if (inputs.adType === bannerType.TargetedAD) {
        formData.append("pageName", inputs.pageName);
        formData.append("models", inputs.models);
      }

      formData.append("status", inputs.status);
      formData.append("state", inputs.state);

      const result = await createPost(formData);

      if (result && result.data) {
        toast.success(result.data.message);
        navigate("/advertisement");
      } else if (result && result.error) {
        toast.error(result.error.data.message);
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  if (isPopularCategoryLoading) {
    return (
      <Skeleton
        sx={{
          transform: "scale(1, 1.0)",
          transformOrigin: "0% 0%",
        }}
        width="100%"
        height="600px"
      />
    );
  }

  if (isError) {
    return <p>{error?.data?.message || "Something went wrong"}</p>;
  }

  const popularSearchCategory = data.data.data;

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        py: 8,
      }}
    >
      <Container maxWidth="md">
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between" spacing={4}>
            <Typography variant="h4">Create Advertisement</Typography>
          </Stack>
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {({ setFieldValue, values, setFieldTouched, setFieldError }) => {
              return (
                <Form>
                  <Stack spacing={3}>
                    <AdTypeWatcher />
                    <FormInput
                      label="Ad Name"
                      name="adName"
                      type="text"
                      placeholder="Enter Advertisement Text"
                    />
                    <FormSelect
                      name="adType"
                      label="Ad Type"
                      items={bannerTypes}
                    />
                    {values.adType === bannerType.TargetedAD && (
                      <FormSelect
                        name="pageName"
                        label="Page Name"
                        items={pageLists}
                      />
                    )}
                    <FormSelect
                      name="bannerPosition"
                      label="Banner Position"
                      items={bannerPostions}
                    />
                    {values.adType === bannerType.TargetedAD &&
                      values.pageName === frontendPages.POPULAR_SEARCH && (
                        <MultiSelectField
                          name="models"
                          label="Popular Search Category"
                          items={popularSearchCategory}
                        />
                      )}
                    {values.adType === bannerType.TargetedAD &&
                      (values.pageName === frontendPages.MODEL ||
                        values.pageName === frontendPages.BRAND) && (
                        <CreatableAutocomplete
                          name="models"
                          label="Models"
                          placeholder="Type and press enter"
                        />
                      )}

                    <FormInput label="Web Link" name="webLink" type="text" />
                    <FormImageInput
                      name="desktopBanner"
                      imageValues={values.desktopBanner}
                      onChangeSelectedImage={(event) => {
                        setFieldTouched(event.target.name);
                        setFieldValue(event.target.name, event.target.files[0]);
                      }}
                      onError={(message) => {
                        setFieldError("desktopBanner", message);
                      }}
                      onCheckRes={(checkResolution) => {
                        setFieldValue("checkResolution", checkResolution);
                      }}
                      checkResolution={values.checkResolution}
                      label="Desktop View"
                    />

                    <FormImageInput
                      name="mobileBanner"
                      imageValues={values.mobileBanner}
                      onChangeSelectedImage={(event) => {
                        setFieldTouched(event.target.name);
                        setFieldValue(event.target.name, event.target.files[0]);
                      }}
                      onError={(message) => {
                        setFieldError("mobileBanner", message);
                      }}
                      onCheckRes={(checkResolution) => {
                        setFieldValue("checkResolution", checkResolution);
                      }}
                      checkResolution={values.checkResolution}
                      label="Mobile View"
                    />
                    <FormSelect
                      name="state"
                      label="State"
                      items={[{ code: "All", name: "All" }, ...states]}
                    />
                    <FormSwitch label="Active Status" name="status" />
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isLoading}
                    >
                      Submit
                    </Button>
                  </Stack>
                </Form>
              );
            }}
          </Formik>
        </Stack>
      </Container>
    </Box>
  );
}

function AdTypeWatcher() {
  const { values, setFieldValue } = useFormikContext();

  const prevAdTypeRef = useRef(values.adType);
  const prevPageNameRef = useRef(values.pageName);

  useEffect(() => {
    // If adType changed to Generic -> reset pageName and models
    if (
      prevAdTypeRef.current !== values.adType &&
      values.adType === "Generic"
    ) {
      setFieldValue("pageName", "");
      setFieldValue("models", []);
    }

    // If pageName changed -> reset models
    if (prevPageNameRef.current !== values.pageName) {
      setFieldValue("models", []);
    }

    // Update refs
    prevAdTypeRef.current = values.adType;
    prevPageNameRef.current = values.pageName;
  }, [values.adType, values.pageName, setFieldValue]);

  return null;
}
